/**
 * Dual-milestone cart progress bar updater.
 * Works with [data-cart-threshold-dual] / [data-cart-calc-shipping].
 *
 * Integration:
 *   1. Include shipping_bar.css + this file
 *   2. Render snippets/cart-threshold-dual.liquid in cart drawer/page
 *   3. Call CartThresholdDual.update(cart) after cart AJAX changes
 *      OR rely on cart:updated / cart:refresh events
 */
(function () {
  'use strict';

  var SELECTOR = '[data-cart-threshold-dual], [data-cart-calc-shipping].t4s-cart-threshold-dual';
  var AUTO_GIFT_PROPERTY = '_auto_gift';
  var isSyncingGift = false;

  function formatMoney(cents) {
    if (window.T4SThemeSP && T4SThemeSP.Currency && typeof T4SThemeSP.Currency.formatMoney === 'function') {
      return T4SThemeSP.Currency.formatMoney(cents); 
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function replaceToken(text, token, value) {
    return String(text || '').split(token).join(value);
  }

  function cartRoute(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    return root + String(path || '').replace(/^\//, '');
  }

  function postCart(path, body) {
    return fetch(cartRoute(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (res) { return res.json(); });
  }

  function getGiftConfig(root) {
    if (!root) return null;

    var config = {
      enabled: root.getAttribute('data-gift-auto') === 'true',
      threshold: parseInt(root.getAttribute('data-gift') || '0', 10) || 0,
      productId: parseInt(root.getAttribute('data-gift-product-id') || '0', 10) || 0,
      variantId: parseInt(root.getAttribute('data-gift-variant-id') || '0', 10) || 0,
    };

    config.enabled = config.enabled && config.threshold > 0 && config.variantId > 0;
    return config;
  }

  function getPrimaryGiftConfig() {
    var roots = document.querySelectorAll(SELECTOR);
    var config = null;

    roots.forEach(function (root) {
      if (config) return;
      var nextConfig = getGiftConfig(root);
      if (nextConfig && nextConfig.enabled) config = nextConfig;
    });

    return config;
  }

  function isGiftLine(item, config) {
    if (!item || !config) return false;

    var productId = parseInt(item.product_id || '0', 10) || 0;
    var variantId = parseInt(item.variant_id || item.id || '0', 10) || 0;

    return (
      (config.variantId > 0 && variantId === config.variantId) ||
      (config.productId > 0 && productId === config.productId)
    );
  }

  function getGiftLines(cart, config) {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter(function (item) {
      return isGiftLine(item, config);
    });
  }

  function getGiftUnitPrice(item) {
    if (!item) return 0;
    if (typeof item.final_price === 'number') return item.final_price;
    if (typeof item.final_line_price === 'number' && typeof item.quantity === 'number' && item.quantity > 0) return item.final_line_price / item.quantity;
    if (typeof item.line_price === 'number' && typeof item.quantity === 'number' && item.quantity > 0) return item.line_price / item.quantity;
    return 0;
  }

  function getQualifyingTotal(cart, config) {
    var total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var giftLine = getGiftLines(cart, config)[0];
    var giftPriceToRemove = getGiftUnitPrice(giftLine);

    return Math.max(0, total - giftPriceToRemove);
  }

  function buildMessage(root, total, shipping, gift) {
    var msgEl = root.querySelector('[data-cart-threshold-msg]') || root.querySelector('.t4s-cart-threshold-msg');
    if (!msgEl) return;

    var textEmpty = root.getAttribute('data-text-empty') || 'Free shipping for orders over {{money}}';
    var textShippingAway = root.getAttribute('data-text-shipping-away') || 'You are {{money}} away from free shipping!';
    var textShippingDone = root.getAttribute('data-text-shipping-done') || 'Congratulations! You have got free shipping!';
    var textGiftAway = root.getAttribute('data-text-gift-away') || 'Spend {{money}} more to get a {{gift}}!';
    var giftName = root.getAttribute('data-gift-name') || 'Free Gift';
    var maxAmount = Math.max(shipping, gift || 0);

    if (total <= 0) {
      var emptyMoney = formatMoney(shipping);
      msgEl.innerHTML = replaceToken(textEmpty, '{{money}}', '<strong>' + emptyMoney + '</strong>');
      return;
    }

    var showShipping = shipping > 0 && total < shipping;
    var showGift = gift > 0 && total < gift;

    if (showShipping && showGift) {
      if (shipping <= gift) {
        showGift = false;
      } else {
        showShipping = false;
      }
    }

    if (showGift) {
      var giftRemaining = formatMoney(gift - total);
      var giftHtml = replaceToken(textGiftAway, '{{money}}', '<span class="t4s-cr">' + giftRemaining + '</span>');
      giftHtml = replaceToken(giftHtml, '{{gift}}', giftName);
      msgEl.innerHTML = giftHtml;
      return;
    }

    if (showShipping) {
      var shipRemaining = formatMoney(shipping - total);
      var shipHtml = replaceToken(textShippingAway, '{{money}}', '<span class="t4s-cr">' + shipRemaining + '</span>');
      msgEl.innerHTML = shipHtml;
      return;
    }

    if (maxAmount === gift && gift > 0) {
      msgEl.innerHTML =
        'Congratulations! You\'ve got a <span class="t4s-cr is--congratulations">' + giftName + '</span>!';
      return;
    }

    msgEl.innerHTML = textShippingDone;
  }

  function updateBar(root, total, shipping, gift) {
    var maxAmount = Math.max(shipping, gift || 0);
    if (maxAmount <= 0) return;

    var progress = Math.min(100, (total * 100) / maxAmount);
    var giftPos = gift > 0 ? (gift * 100) / maxAmount : 0;
    var shippingPos = (shipping * 100) / maxAmount;

    var progressEl = root.querySelector('[data-cart-threshold-progress]') || root.querySelector('.t4s-cart-threshold-progress');
    if (progressEl) progressEl.style.width = progress + '%';

    var giftMarker = root.querySelector('[data-cart-threshold-marker="gift"]') || root.querySelector('.t4s-marker-gift');
    if (giftMarker) {
      if (gift > 0) giftMarker.style.left = giftPos + '%';
      giftMarker.classList.toggle('is--reached', gift > 0 && total >= gift);
    }

    var shipMarker = root.querySelector('[data-cart-threshold-marker="shipping"]') || root.querySelector('.t4s-marker-shipping');
    if (shipMarker) {
      shipMarker.style.left = shippingPos + '%';
      shipMarker.classList.toggle('is--reached', total >= shipping);
    }

    root.setAttribute('data-total', String(total));
  }

  function updateOne(root, cart) {
    if (!root) return;

    var shipping = parseInt(root.getAttribute('data-shipping') || '0', 10) || 0;
    var gift = parseInt(root.getAttribute('data-gift') || '0', 10) || 0;
    var total = cart && typeof cart.total_price === 'number'
      ? cart.total_price
      : parseInt(root.getAttribute('data-total') || '0', 10) || 0;
    var giftConfig = getGiftConfig(root);

    if (cart && giftConfig && giftConfig.enabled) {
      total = getQualifyingTotal(cart, giftConfig);
    }

    buildMessage(root, total, shipping, gift);
    updateBar(root, total, shipping, gift);
  }

  function updateAll(cart) {
    document.querySelectorAll(SELECTOR).forEach(function (root) {
      updateOne(root, cart);
    });
  }

  function refreshCartUi() {
    if (window.T4SThemeSP && T4SThemeSP.Cart && typeof T4SThemeSP.Cart.getToFetch === 'function') {
      T4SThemeSP.Cart.getToFetch();
      return;
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function addGift(config) {
    return postCart('/cart/add.js', {
      items: [
        {
          id: config.variantId,
          quantity: 1,
          properties: {
            [AUTO_GIFT_PROPERTY]: 'true',
          },
        },
      ],
    });
  }

  function removeGiftLines(lines) {
    return lines.reduce(function (promise, line) {
      return promise.then(function () {
        return postCart('/cart/change.js', {
          id: line.key || line.variant_id || line.id,
          quantity: 0,
        });
      });
    }, Promise.resolve());
  }

  function normalizeGiftLines(lines) {
    return lines.reduce(function (promise, line, index) {
      return promise.then(function () {
        return postCart('/cart/change.js', {
          id: line.key || line.variant_id || line.id,
          quantity: index === 0 ? 1 : 0,
        });
      });
    }, Promise.resolve());
  }

  function needsGiftNormalize(lines) {
    if (lines.length !== 1) return true;
    return lines[0].quantity !== 1;
  }

  function syncAutoGift(cart) {
    var config = getPrimaryGiftConfig();
    if (!config || !cart || isSyncingGift) return Promise.resolve(cart);

    var giftLines = getGiftLines(cart, config);
    var total = getQualifyingTotal(cart, config);
    var syncPromise = null;

    if (total >= config.threshold && giftLines.length === 0) {
      syncPromise = addGift(config);
    } else if (total >= config.threshold && needsGiftNormalize(giftLines)) {
      syncPromise = normalizeGiftLines(giftLines);
    } else if (total < config.threshold && giftLines.length > 0) {
      syncPromise = removeGiftLines(giftLines);
    }

    if (!syncPromise) return Promise.resolve(cart);

    isSyncingGift = true;
    return syncPromise
      .then(function (updatedCart) {
        refreshCartUi();
        return updatedCart || cart;
      })
      .catch(function () {
        return cart;
      })
      .finally(function () {
        isSyncingGift = false;
      });
  }

  function fetchCartAndUpdate() {
    return fetch(cartRoute('/cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateAll(cart);
        return syncAutoGift(cart);
      })
      .catch(function () {});
  }

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      updateAll(event.detail.cart);
      syncAutoGift(event.detail.cart);
      return;
    }
    fetchCartAndUpdate();
  });

  document.addEventListener('cart:refresh', fetchCartAndUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCartAndUpdate, { once: true });
  } else {
    fetchCartAndUpdate();
  }

  window.CartThresholdDual = {
    update: function (cart) {
      updateAll(cart);
      return syncAutoGift(cart);
    },
    refresh: fetchCartAndUpdate,
    syncGift: syncAutoGift,
  };
})();
